import { useState } from 'react';
import PropTypes from 'prop-types';
import { Editor } from '@tinymce/tinymce-react';
import { ImageIcon, FileEditIcon, EyeIcon } from 'lucide-react';

const BlogForm = ({ initialData, onSubmit }) => {
  const [formData, setFormData] = useState({
    title: initialData?.title || '',
    author: initialData?.author || 'Nutrition Freak',
    content: initialData?.content || '',
    heroImage: initialData?.heroImage || '',
    excerpt: initialData?.excerpt || '',
    readTime: initialData?.readTime || '5 min read',
    published: initialData?.published || true
  });

  const [previewMode, setPreviewMode] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="max-w-4xl mx-auto space-y-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-2 text-xl font-semibold">
              <FileEditIcon className="w-5 h-5" />
              {initialData ? 'Edit Blog Post' : 'Create New Blog Post'}
            </span>
            <button
              type="button"
              onClick={() => setPreviewMode(!previewMode)}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md border border-gray-300 bg-white hover:bg-gray-50"
            >
              <EyeIcon className="w-4 h-4" />
              {previewMode ? 'Edit Mode' : 'Preview'}
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Title Input */}
          <div className="space-y-2">
            <label htmlFor="title" className="block text-sm font-medium text-gray-700">
              Title
            </label>
            <input
              id="title"
              type="text"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              placeholder="Enter your blog title"
              className="w-full px-3 py-2 text-lg border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          {/* Hero Image Section */}
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700" htmlFor="heroImage">
              <ImageIcon className="w-4 h-4" />
              Hero Image URL
            </label>
            <input
              id="heroImage"
              type="url"
              value={formData.heroImage}
              onChange={(e) => setFormData(prev => ({ ...prev, heroImage: e.target.value }))}
              placeholder="Enter image URL"
              className="w-full px-3 py-2 font-mono text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {formData.heroImage && (
              <div className="mt-2 rounded-lg overflow-hidden border border-gray-200">
                <img
                  src={formData.heroImage}
                  alt="Hero preview"
                  className="w-full h-48 object-cover"
                  onError={(e) => e.target.style.display = 'none'}
                />
              </div>
            )}
          </div>

          <hr className="border-t border-gray-200" />

          {/* Excerpt Section */}
          <div className="space-y-2">
            <label htmlFor="excerpt" className="block text-sm font-medium text-gray-700">
              Excerpt
            </label>
            <textarea
              id="excerpt"
              value={formData.excerpt}
              onChange={(e) => setFormData(prev => ({ ...prev, excerpt: e.target.value }))}
              placeholder="Write a brief summary of your blog post"
              className="w-full h-24 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Main Content Editor */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Content</label>
            <div className="border rounded-lg">
              <Editor
                apiKey={process.env.REACT_APP_TINYMCE_API_KEY}
                value={formData.content}
                onEditorChange={(content) => setFormData(prev => ({ ...prev, content }))}
                init={{
                  height: 500,
                  menubar: true,
                  plugins: [
                    'advlist', 'autolink', 'lists', 'link', 'image', 'charmap',
                    'preview', 'anchor', 'searchreplace', 'visualblocks', 'code',
                    'fullscreen', 'insertdatetime', 'media', 'table', 'code', 'help',
                    'wordcount'
                  ],
                  toolbar: 'undo redo | blocks | bold italic | alignleft aligncenter alignright | bullist numlist outdent indent | help',
                  skin: 'oxide',
                  content_css: 'default'
                }}
              />
            </div>
          </div>
          {/* Read Time Input */}
          <div className="flex items-center justify-between pt-4">
            <div className="space-y-2">
              <label htmlFor="readTime" className="block text-sm font-medium text-gray-700">
                Estimated Read Time
              </label>
              <input
                id="readTime"
                type="text"
                value={formData.readTime}
                onChange={(e) => setFormData(prev => ({ ...prev, readTime: e.target.value }))}
                placeholder="e.g. 5 min read"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Publish Toggle */}
          <div className="flex items-center justify-between pt-4">
            <div className="space-y-0.5">
              <label className="block text-sm font-medium text-gray-700">Publishing Status</label>
              <div className="text-sm text-gray-500">
                {formData.published ? 'Your post will be visible to readers' : 'Draft mode - only visible to you'}
              </div>
            </div>
            <div className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={formData.published}
                onChange={(e) => setFormData(prev => ({ ...prev, published: e.target.checked }))}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-between p-6 border-t border-gray-200">
          <button
            type="button"
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            onClick={() => {
              setFormData(prev => ({ ...prev, published: false }));
              onSubmit({ ...formData, published: false });
            }}
          >
            Save as Draft
          </button>
          <button
            type="submit"
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
          >
            {initialData ? 'Update Post' : 'Publish Post'}
          </button>
        </div>
      </div>

      {!formData.title && (
        <div className="p-4 border-l-4 border-yellow-400 bg-yellow-50">
          <p className="text-sm text-yellow-700">
            Start by giving your blog post a title above
          </p>
        </div>
      )}
    </form>
  );
};

BlogForm.propTypes = {
  initialData: PropTypes.object,
  onSubmit: PropTypes.func.isRequired
};

export default BlogForm;
